import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BRANCHES } from "@/lib/branches";
import { apiClient } from "@/lib/api";
import { KonattuLogo } from "@/lib/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Instagram, QrCode } from "lucide-react";

const INSTAGRAM_HANDLE = "konattugroup";
const INSTAGRAM_URL = `https://www.instagram.com/${INSTAGRAM_HANDLE}/`;
// On mobile, this deep link opens the Instagram app directly if installed.
const INSTAGRAM_DEEPLINK = `instagram://user?username=${INSTAGRAM_HANDLE}`;
// Auto-redirect delay (ms) after successful registration
const IG_REDIRECT_DELAY_MS = 2500;

function openInstagram() {
  // Try app deep link first (mobile), fall back to web URL.
  try {
    const w = window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer");
    if (!w) {
      // Popup blocked – navigate same tab
      window.location.href = INSTAGRAM_URL;
    }
  } catch {
    window.location.href = INSTAGRAM_URL;
  }
}

export default function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const branchIdxParam = searchParams.get("b");
  const lockedIdx = branchIdxParam !== null && !isNaN(parseInt(branchIdxParam, 10))
    ? Math.max(0, Math.min(BRANCHES.length - 1, parseInt(branchIdxParam, 10)))
    : null;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [invoice, setInvoice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successName, setSuccessName] = useState("");
  const [countdown, setCountdown] = useState(Math.ceil(IG_REDIRECT_DELAY_MS / 1000));

  const nameRef = React.useRef(null);

  useEffect(() => {
    if (lockedIdx !== null && nameRef.current) {
      setTimeout(() => nameRef.current?.focus(), 250);
    }
  }, [lockedIdx]);

  const phoneValid = useMemo(() => /^[6-9]\d{9}$/.test(phone), [phone]);
  const invoiceValid = useMemo(() => /^[A-Za-z0-9\-/#.]{3,40}$/.test(invoice.trim()), [invoice]);
  const canSubmit = lockedIdx !== null
    && name.trim().length >= 2
    && phoneValid
    && invoiceValid
    && !submitting;

  // Auto-redirect to Instagram after successful registration
  useEffect(() => {
    if (!successName) return;
    setCountdown(Math.ceil(IG_REDIRECT_DELAY_MS / 1000));
    const tick = setInterval(() => {
      setCountdown((c) => (c > 1 ? c - 1 : 0));
    }, 1000);
    const t = setTimeout(() => {
      openInstagram();
    }, IG_REDIRECT_DELAY_MS);
    return () => { clearTimeout(t); clearInterval(tick); };
  }, [successName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await apiClient.post("/register", {
        branch: BRANCHES[lockedIdx],
        name: name.trim(),
        phone,
        invoice_number: invoice.trim().toUpperCase(),
      });
      setSuccessName(name.trim());
      setName("");
      setPhone("");
      setInvoice("");
      toast.success("Registered successfully!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Registration failed. Please check your details.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- No QR scanned: show scan-QR screen ----------
  if (lockedIdx === null) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-white">
        <div className="max-w-md w-full text-center" data-testid="scan-qr-screen">
          <KonattuLogo className="h-20 md:h-24" />
          <div className="kg-card kg-card-red-top mt-8 p-7">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{background: "#FFE9E9"}}>
                <QrCode className="w-8 h-8" style={{color: "#CC0000"}} />
              </div>
            </div>
            <h1 className="font-display text-2xl md:text-3xl text-neutral-900 mt-5 leading-tight">
              Please scan the branch QR code
            </h1>
            <p className="text-neutral-600 mt-3 text-sm">
              Visit any Konattu Group store and scan the Lucky Draw QR code at the counter to enter today&apos;s draw.
            </p>

            <div className="mt-6 pt-6 border-t border-neutral-100">
              <Link to="/winners" className="inline-block text-sm font-medium" style={{color:"#CC0000"}} data-testid="view-winners-link">
                View Today&apos;s Winners →
              </Link>
            </div>
          </div>
          <p className="mt-8 text-xs text-neutral-400">Konattu Group · {new Date().getFullYear()}</p>
        </div>
      </div>
    );
  }

  // ---------- Success screen with auto IG redirect ----------
  if (successName) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-white">
        <div className="kg-card kg-card-red-top max-w-md w-full p-8 text-center fade-up" data-testid="registration-success">
          <div className="flex justify-center mb-6">
            <div className="tick-circle">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h2 className="font-display text-3xl text-neutral-900 mb-2">🎉 You&apos;re registered!</h2>
          <p className="text-neutral-700 text-lg">Good luck, <span className="font-semibold" style={{color:"#CC0000"}}>{successName}</span>!</p>
          <p className="text-neutral-500 mt-2 text-sm">Your entry is in <strong>tomorrow morning&apos;s</strong> draw at this branch.</p>

          <div className="mt-6 pt-6 border-t border-neutral-100">
            <p className="font-display text-xl text-neutral-900">
              Don&apos;t miss our updates!
            </p>
            <p className="text-sm text-neutral-600 mt-1">
              Follow <span className="font-semibold" style={{color:"#CC0000"}}>@{INSTAGRAM_HANDLE}</span> to see today&apos;s winner reveal first.
            </p>
            <Button
              type="button"
              onClick={openInstagram}
              className="mt-4 w-full h-14 text-base text-white font-semibold"
              style={{ background: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
              data-testid="success-instagram-btn"
            >
              <Instagram className="w-5 h-5 mr-2" />
              Follow @{INSTAGRAM_HANDLE}
            </Button>
            {countdown > 0 ? (
              <p className="text-xs text-neutral-500 mt-3" data-testid="ig-redirect-countdown">
                Opening Instagram in {countdown} second{countdown === 1 ? "" : "s"}…
              </p>
            ) : (
              <p className="text-xs text-neutral-500 mt-3" data-testid="ig-redirect-done">
                Instagram opened. If nothing happened, tap the button above.
              </p>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              className="text-sm text-neutral-600 hover:text-neutral-900"
              onClick={() => setSuccessName("")}
              data-testid="register-another-btn"
            >
              Register another person
            </button>
            <Link to="/winners" className="text-sm" style={{color:"#CC0000"}} data-testid="view-winners-link-success">
              View Today&apos;s Winners →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Branch-locked registration form (simple, no IG gate) ----------
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-5 pt-10 pb-24">
        <div className="mb-8">
          <KonattuLogo className="h-20 md:h-24" />
        </div>

        <div className="kg-card kg-card-red-left p-6">
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900 leading-tight">
            Lucky Draw Entry
          </h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Today&apos;s bill is eligible for <strong>tomorrow morning&apos;s</strong> lucky draw at this branch.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <Label className="text-neutral-700 text-sm">Branch</Label>
              <div
                className="mt-2 p-4 rounded-xl text-white font-semibold flex items-center justify-between"
                style={{ background: "#CC0000" }}
                data-testid="locked-branch"
              >
                <span className="text-sm md:text-base">{BRANCHES[lockedIdx]}</span>
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Verified</span>
              </div>
            </div>

            <div>
              <Label htmlFor="name-input" className="text-neutral-700">Full Name</Label>
              <Input
                id="name-input"
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="kg-input mt-2 h-12 border-2"
                data-testid="name-input"
                maxLength={80}
              />
            </div>

            <div>
              <Label htmlFor="phone-input" className="text-neutral-700">Mobile Number</Label>
              <Input
                id="phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                placeholder="10 digit mobile"
                className="kg-input mt-2 h-12 border-2"
                data-testid="phone-input"
              />
              {phone && !phoneValid && (
                <p className="text-xs text-red-600 mt-1">Enter valid 10-digit number starting with 6-9.</p>
              )}
            </div>

            <div>
              <Label htmlFor="invoice-input" className="text-neutral-700">Invoice / Bill Number</Label>
              <Input
                id="invoice-input"
                value={invoice}
                onChange={(e) => setInvoice(e.target.value.toUpperCase().slice(0, 40))}
                placeholder="e.g. INV-2026-001234"
                className="kg-input mt-2 h-12 border-2 font-mono"
                data-testid="invoice-input"
                autoComplete="off"
              />
              <p className="text-xs text-neutral-500 mt-1">Found on your bill/receipt. Required to verify your purchase.</p>
              {invoice && !invoiceValid && (
                <p className="text-xs text-red-600 mt-1">3–40 characters. Only letters, numbers and - / # . allowed.</p>
              )}
            </div>

            <p className="text-xs text-neutral-500 text-center -mb-1">
              You&apos;ll be invited to follow us on Instagram after submitting.
            </p>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="kg-btn-red w-full h-14 text-base mt-2"
              data-testid="register-submit-btn"
            >
              {submitting ? "Registering..." : "Register Now"}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link to="/winners" className="text-sm font-medium" style={{color:"#CC0000"}} data-testid="view-winners-link">
            View Today&apos;s Winners →
          </Link>
        </div>

        <p className="mt-10 text-center text-xs text-neutral-400">
          Konattu Group · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
